import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import { useQuery } from '@apollo/client/react';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { PATH_USER_VIDEOS } from '@jwp/ott-common/src/paths';
import { GET_MY_VIDEOS, GetMyVideosData, GetMyVideosVariables } from '@jwp/ott-common/src/graphql/query/reels.queries';
import env from '@jwp/ott-common/src/env';

import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import VideoPreview from '../../components/VideoPreview/VideoPreview';

const VideoPreviewPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { file, previewUrl, values, videoValues, returnPath } = useUploadStore();
  const urlReturnPath = useQueryParam('returnPath');
  const typeParam = useQueryParam('type');
  const effectiveReturnPath = returnPath || urlReturnPath;

  const queryVariables = useMemo(() => ({
    filter: { maId: { equalTo: id || '' } },
    first: 1,
    offset: 0,
  }), [id]);

  const { data, loading } = useQuery<GetMyVideosData, GetMyVideosVariables>(GET_MY_VIDEOS, {
    variables: queryVariables,
    skip: !id,
  });

  const videoData = data?.allMediaApprovals?.nodes?.[0];

  const isVideoType = typeParam === 'video';

  const effectiveTitle = useMemo(() => {
    if (id && videoData) return videoData.maTitle;
    return isVideoType ? videoValues.title : values.title;
  }, [id, videoData, isVideoType, videoValues.title, values.title]);

  const effectiveDescription = useMemo(() => {
    if (id && videoData) return videoData.maDescription;
    return isVideoType ? videoValues.description : values.description;
  }, [id, videoData, isVideoType, videoValues.description, values.description]);

  const effectiveCategory = useMemo(() => {
    if (id && videoData) return videoData.maCategory;
    return isVideoType ? undefined : values.category;
  }, [id, videoData, isVideoType, values.category]);

  const effectiveAuthor = useMemo(() => {
    if (id && videoData) {
      return {
        name: videoData.userByMaUploadedBy?.uFullName || null,
        avatarUrl: videoData.userByMaUploadedBy?.uAvatarUrl || null,
      };
    }
    return undefined;
  }, [id, videoData]);

  const isReel = videoData ? videoData.maType === 'REELS' : typeParam !== 'video';

  useEffect(() => {
    // If not viewing an existing video (no id param) and no file/previewUrl in memory, redirect.
    if (!id && !file && !previewUrl) {
      const fallbackPath = effectiveReturnPath || `${PATH_USER_VIDEOS}?u=upload-reel`;
      navigate(fallbackPath, { replace: true });
    }
  }, [id, file, previewUrl, navigate, effectiveReturnPath]);

  if (!id && !file && !previewUrl) {
    return null;
  }

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000' }}>
        <LoadingOverlay inline />
      </div>
    );
  }

  const effectiveFile = id ? null : file;
  const effectivePreviewUrl = id && videoData 
    ? `${env.APP_BASE_URL || ''}${videoData.maS3MediaKeyUrl}`
    : previewUrl;

  return (
    <VideoPreview
      file={effectiveFile}
      previewUrl={effectivePreviewUrl}
      title={effectiveTitle}
      description={effectiveDescription}
      category={effectiveCategory}
      author={effectiveAuthor}
      showDetails={!!id}
      isReel={isReel}
      onClose={() => {
        if (effectiveReturnPath) {
          navigate(effectiveReturnPath);
        } else {
          navigate(`${PATH_USER_VIDEOS}`);
        }
      }}
    />
  );
};

export default VideoPreviewPage;
